import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { 
	Typography, 
	Box, 
	Paper, 
	Alert, 
	AlertTitle, 
	Stack, 
	Button,
	SxProps,
	Theme
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

interface TitleWithIconProps {
	icon: React.ElementType;
	children: ReactNode;
	sx?: SxProps<Theme>;
}

interface NotificationPaperProps {
	children: ReactNode;
	sx?: SxProps<Theme>;
}

interface StyledAlertProps {
	children: ReactNode;
	severity?: 'info' | 'warning' | 'error' | 'success';
	sx?: SxProps<Theme>;
}

interface ActionButtonProps {
	children: ReactNode;
	onClick?: () => void;
	sx?: SxProps<Theme>;
}

interface PopupProps {
	opened: boolean;
	onClose: () => void;
	children: ReactNode;
}

interface NoMatchPopupProps {
	opened: boolean;
	error: any;
	redirectUrl?: string;
}

export const TitleWithIcon: FC<TitleWithIconProps> = ({ icon: Icon, children, sx }) => (
	<Typography 
		variant="h6" 
		sx={{ 
			mb: 2, 
			fontWeight: 'bold', 
			display: 'flex', 
			alignItems: 'center',
			...sx
		}}
	>
		<Icon sx={{ color: 'primary.main', mr: 1 }} />
		{children}
	</Typography>
);

export const NotificationPaper: FC<NotificationPaperProps> = ({ children, sx }) => (
	<Paper 
		elevation={0}
		sx={{ 
			bgcolor: '#eff6ff', 
			borderRadius: 1,
			border: 1,
			borderColor: '#bfdbfe',
			p: 2,
			mb: 2,
			...sx
		}}
	>
		{children}
	</Paper>
);

export const AlertNotification: FC<PropsWithChildren> = ({ children }) => (
	<Alert 
		severity='info'
		sx={{ 
			bgcolor: 'transparent', 
			p: 0,
		}}
	>
		<AlertTitle sx={{ fontWeight: 'bold', color: 'black' }}>
			{children}
		</AlertTitle>
	</Alert>
)

export const ActionButton: FC<ActionButtonProps> = ({ children, onClick }) => (
	<Button 
		variant="contained" 
		color="primary" 
		fullWidth
		onClick={onClick}
		sx={{
			py: 1.5,
			borderRadius: 3,
		}}
	>
		{children}
	</Button>
);

export const Popup: FC<PopupProps> = ({ opened, onClose, children }) => {
	return opened ? (
		<div className="popup">
			{children}
		</div>
	) : null;
};

export const NotificationBox: FC<PropsWithChildren> = ({children}) => 
	<Box sx={{
			backgroundColor: 'white',
			borderRadius: '12px',
			boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
			padding: '24px',
			width: '100%',
			maxWidth: '500px',
			margin: 'auto',
			position: 'relative',
	}}>
		{children}
	</Box>
